/* ============================================================================
   olmia-wishlist.js — window.OlmiaWishlist: localStorage verlanglijst.
   Hart-knop (.pcard__fav[data-wish]) + header teller ([data-wish-count]).

   ALLEEN DE HANDLE — GEEN MOMENTOPNAME (gewijzigd 2026-08-20, ROADBLOCKS #74)
   -----------------------------------------------------------------------------
   Tot vandaag bewaarde dit bestand de complete `data-product`-JSON van de kaart:
   titel, foto, prijs, klantprijs, tier-vlaggen. Twee dingen gingen daar mis, en
   allebei zijn ze ernstig:

   1. EEN BEVROREN POORT. Die momentopname legde óók de prijsmuur-beslissing vast
      op het moment van klikken. Een gast die bij UR15 op het hartje tikt bewaart
      `price: null` (correct — de gastenmuur schermt af); logt diezelfde bezoeker
      daarna in, dan bleef zijn verlanglijst "Prijs op aanvraag · Offerte" tonen
      voor een leverbaar product waarvan diezelfde klant op /products/ur15 gewoon
      het bedrag ziet. (De gemeten bedragen staan in ROADBLOCKS #74 — niet hier:
      dit bestand is een asset en staat zonder sessie op de CDN.)
      Precies de trechter die deze winkel bouwt ("Log in voor prijzen") eindigde in
      een offerteformulier voor een leverbaar product.
   2. GELD OP HET APPARAAT. Andersom bleef een oude momentopname met `price` én
      `cust` (de B2B-inkoopprijs) staan, ook nadat de winkel hem had afgeschermd.
      Gemeten: een gast met een `data-cust`-attribuut in de DOM. Dat is ROADBLOCKS #18
      op een oppervlak dat die regel nooit had gekregen.

   De lijst draagt daarom nog uitsluitend `{handle}`. De verlanglijstpagina haalt
   elke kaart server-side op (sections/wishlist.liquid → snippets/product-card.liquid),
   dus de poortbeslissing valt weer op één plek en is per paginabezoek vers.
   `sanitize()` hieronder haalt bovendien de oude momentopnames van het apparaat
   af zodra iemand de winkel weer bezoekt — repareren zonder wissen is hier niet
   genoeg, de bedragen stáán er nog.

   TOESTEMMING — WAAROM HIER GEEN COOKIEMUUR OMHEEN STAAT (beoordeeld 2026-08-19)
   -----------------------------------------------------------------------------
   Gemeten in een echte browser als gast (scripts/verify-toestemming.mjs): bij het
   laden van /collections/cobots schrijft dit bestand NIETS. `olmia_wishlist_v1`
   verschijnt pas na de eerste klik op een hartje. Er zit geen identificatie in —
   alleen producthandles met de kaartgegevens die al op het scherm stonden — en er
   gaat geen byte van naar een derde partij of naar de server.

   Dat is opslag die de bezoeker zélf vraagt, voor een dienst die hij zélf start:
   de uitzondering van artikel 5(3) ePrivacy / artikel 11.7a lid 3 Telecommunicatiewet,
   dezelfde grond als de winkelwagen. De EDPB rekent "user-input"- en
   winkelwagenopslag daar expliciet toe (Richtlijnen 2/2023 over de reikwijdte van
   artikel 5(3) ePrivacy, §§ 55-58). Er is dus geen toestemming nodig, en die eisen
   zou de functie kapotmaken: een verlanglijst die niets onthoudt is geen
   verlanglijst.

   Wat er wél moest gebeuren: BEWAARTERMIJN. localStorage kent geen vervaldatum, dus
   zonder ingreep staat de lijst er over vijf jaar nog. Opslagbeperking (AVG art. 5
   lid 1 sub e) vraagt een grens. De lijst krijgt daarom een tijdstempel en vervalt
   na MAX_AGE_DAYS; elke wijziging zet de klok terug, dus wie hem gebruikt raakt hem
   nooit kwijt. Is de lijst leeg, dan wordt de sleutel VERWIJDERD in plaats van als
   lege array bewaard — niets opslaan is beter dan niets opslaan in een sleutel.

   Formaat: {v:1, t:<ms>, items:[…]}. Een KALE ARRAY blijft leesbaar (oud formaat en
   scripts/verify-editor.mjs r441 zetten die neer) en wordt bij de eerstvolgende
   wijziging omgezet.
   ========================================================================== */
window.OlmiaWishlist = (function () {
  // Vertaalbare teksten (window.olmiaT uit de layout); de tweede parameter is de Nederlandse terugval.
  const T = (k, f) => (window.olmiaT ? window.olmiaT(k, f) : f);
  const KEY = 'olmia_wishlist_v1';
  const MAX_AGE_DAYS = 180;
  const MAX_AGE = MAX_AGE_DAYS * 86400000;
  let items = [];
  let rewriteNeeded = false;   // gezet door load(): er stond meer in dan een handle
  let loadedAt = 0;
  const subs = [];

  /* Meteen inlezen én opschonen (de functiedeclaraties hieronder zijn gehoist).
     Stond er nog een oude momentopname mét `price` en `cust` op dit apparaat, dan
     is die hierna weg — zonder de bewaarklok terug te zetten (`loadedAt`). */
  items = load();
  if (rewriteNeeded) persist(loadedAt || undefined);

  /* Alles wat GEEN handle is, gaat eruit: prijs, klantprijs, titel, foto, de
     bevroren tier-vlaggen. Dat geldt ook voor wat er vandaag al op een apparaat
     staat — een lijst die de oude momentopname blijft dragen, blijft een bedrag
     dragen. Dubbele handles worden ontdubbeld. */
  function sanitize(list) {
    const out = [];
    let changedShape = false;
    for (const it of (Array.isArray(list) ? list : [])) {
      const h = typeof it === 'string' ? it : (it && typeof it === 'object' ? it.handle : null);
      if (!h || typeof h !== 'string') { changedShape = true; continue; }
      if (out.some(x => x.handle === h)) { changedShape = true; continue; }
      if (typeof it !== 'object' || Object.keys(it).length !== 1) changedShape = true;
      out.push({ handle: h });
    }
    return { items: out, changedShape };
  }

  function load() {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return [];
      const v = JSON.parse(raw);
      let list;
      if (Array.isArray(v)) { list = v; rewriteNeeded = true; }   // oud/kaal formaat
      else if (v && Array.isArray(v.items)) {
        const t = +v.t || 0;
        if (t && (Date.now() - t) > MAX_AGE) { drop(); return []; }
        loadedAt = t;
        list = v.items;
      } else return [];
      const s = sanitize(list);
      if (s.changedShape) rewriteNeeded = true;
      return s.items;
    } catch (e) { return []; }
  }
  function drop() { try { localStorage.removeItem(KEY); } catch (e) {} }
  /* `at` houdt de ORIGINELE tijdstempel vast wanneer we alleen opschonen: een
     sanering is geen gebruik, dus die mag de bewaartermijn niet verlengen. */
  function persist(at) {
    if (!items.length) return drop();                       // leeg = niets bewaren
    try { localStorage.setItem(KEY, JSON.stringify({ v: 1, t: at || Date.now(), items: items })); } catch (e) {}
  }
  function changed() { persist(); subs.forEach(f => { try { f(items); } catch (e) { console.error(e); } }); syncUI(); }

  function has(handle) { return items.some(s => s && s.handle === handle); }
  function indexOf(handle) { return items.findIndex(s => s && s.handle === handle); }

  /* Resolve a handle from either an object, a string or a DOM element. Er wordt
     bewust NIETS meer uit `data-product` gelezen: de kaartgegevens horen niet in
     de opslag thuis. */
  function handleFrom(input) {
    if (!input) return null;
    if (typeof input === 'string') return input;
    if (input.nodeType === 1) {
      const btn = input.closest('[data-wish]') || input;
      const h = btn.getAttribute && btn.getAttribute('data-wish');
      if (h) return h;
      const card = input.closest('.pcard');
      const fav = card && card.querySelector('[data-wish]');
      return fav ? fav.getAttribute('data-wish') : null;
    }
    if (typeof input === 'object' && input.handle) return input.handle;
    return null;
  }

  function toggle(input) {
    const handle = handleFrom(input);
    if (!handle) return false;
    const i = indexOf(handle);
    if (i !== -1) { items.splice(i, 1); changed(); return false; }
    items.push({ handle: handle }); changed(); return true;
  }

  /* Stil verwijderen: gebruikt door de verlanglijstpagina voor handles waarvoor
     de server geen kaart (meer) levert — een verwijderd of verzonnen product.
     Roept met opzet GEEN subscribers aan: de pagina is op dat moment al aan het
     tekenen, en een her-render zou een lus opleveren. */
  function prune(handles) {
    const kill = new Set(Array.isArray(handles) ? handles : [handles]);
    const before = items.length;
    items = items.filter(s => !(s && kill.has(s.handle)));
    if (items.length === before) return 0;
    persist(loadedAt || undefined);
    syncUI();
    return before - items.length;
  }

  function itemsList() { return items.slice(); }
  function count() { return items.length; }
  function subscribe(fn) { subs.push(fn); return fn; }

  /* PRESTATIE - WAAROM HIER EEN VERGELIJKING VOOR ELKE SCHRIJFACTIE STAAT
     (gemeten 2026-08-25, scripts/verify-scriptlast.mjs groep E)

     `el.textContent = getal` vervangt ALTIJD de tekstnode, ook wanneer het getal
     precies hetzelfde blijft. Dat levert dus altijd een childList-mutatie op. De
     MutationObserver onderaan dit bestand kijkt naar childList op de hele body,
     zag die mutatie, en plande daarop de volgende syncUI - die weer schreef, die
     weer een mutatie gaf. Een zelfvoedende requestAnimationFrame-lus, op ELKE
     pagina van de winkel, zolang het tabblad openstond.

     Gemeten in een echte browser, vijf seconden stilstand na het laden:
       320 syncUI-aanroepen (= 64 per seconde, precies elke beeldopbouw)
       956 childList-mutaties op de teller in de koptekst
     en Lighthouse (bootup-time, mobiel, mediaan van 3 runs) schreef dit bestand
     van 9,9 KB daarvoor toe:
       homepage 590 ms · collectie 695 ms · PDP 622 ms · winkelwagen 411 ms
       keuzehulp 411 ms · contact 565 ms
     Dat was 83 tot 100 procent van ALLE hoofddraadtijd van thema-JavaScript op
     die paginas - meer dan gsap, ScrollTrigger, Lenis en motion.js samen, voor
     een teller die nul keer van waarde veranderde.

     De regel: schrijf alleen wat verandert. Dan dooft de lus bij zichzelf uit,
     zonder dat er ook maar iets aan het gedrag verandert. */
  function syncUI() {
    const n = String(items.length);
    const aan = items.length > 0;
    document.querySelectorAll('[data-wish-count]').forEach(el => {
      if (el.textContent !== n) el.textContent = n;
      el.classList.toggle('has-items', aan);
    });
    document.querySelectorAll('[data-wish]').forEach(b => b.classList.toggle('is-active', has(b.getAttribute('data-wish'))));
  }

  /* hart-klik overal (gedelegeerd, werkt ook voor later gerenderde kaarten) */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-wish]'); if (!b) return;
    e.preventDefault();
    const now = toggle(b);
    if (window.OlmiaUI && OlmiaUI.toast) OlmiaUI.toast(now ? T('tools.wishlist_added', 'Toegevoegd aan je verlanglijst') : T('tools.wishlist_removed', 'Verwijderd van je verlanglijst'));
  });

  /* keep .is-active in sync when cards are (re)rendered by filters/search/compare */
  if ('MutationObserver' in window) {
    let scheduled = false;
    /* TWEEDE GRENDEL: alleen hertekenen wanneer er werkelijk hartjes of tellers
       BIJ zijn gekomen. De rest van de winkel muteert de body voortdurend om
       redenen die niets met de verlanglijst te maken hebben: de winkelwagenlade
       die zich vult, een toast, de klonen van de heldencarrousel, de
       vastzet-hulpelementen van ScrollTrigger. Elke zo'n mutatie kostte hiervoor
       twee querySelectorAll over de HELE pagina - op /collections/alle-producten
       zijn dat 48 hartjes per ronde, en na het nahalen van de overige
       serverpaginas 177. Dit filter kost alleen iets wanneer er echt elementen
       zijn toegevoegd, en laat precies de gevallen door waarvoor de observer
       bestaat: filters, zoeken, de vergelijker en de verlanglijstpagina zetten
       nieuwe .pcard-kaarten met hun eigen hartje neer. */
    const raaktVerlanglijst = (muts) => {
      for (let i = 0; i < muts.length; i++) {
        const added = muts[i].addedNodes;
        for (let j = 0; j < added.length; j++) {
          const el = added[j];
          if (!el || el.nodeType !== 1) continue;
          if (el.matches && el.matches('[data-wish],[data-wish-count],.pcard')) return true;
          if (el.querySelector && el.querySelector('[data-wish],[data-wish-count]')) return true;
        }
      }
      return false;
    };
    const mo = new MutationObserver((muts) => {
      if (scheduled || !raaktVerlanglijst(muts)) return;
      scheduled = true;
      requestAnimationFrame(() => { scheduled = false; syncUI(); });
    });
    const start = () => { if (document.body) mo.observe(document.body, { childList: true, subtree: true }); };
    if (document.readyState !== 'loading') start(); else document.addEventListener('DOMContentLoaded', start);
  }

  if (document.readyState !== 'loading') syncUI();
  else document.addEventListener('DOMContentLoaded', syncUI);

  return { has: has, toggle: toggle, items: itemsList, count: count, subscribe: subscribe, syncUI: syncUI, prune: prune };
})();
